import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { 
    Table, 
    TableBody, 
    TableCell, 
    TableHead, 
    TableHeader, 
    TableRow 
} from '../../components/ui/table';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import {
    Building2,
    Search,
    Trash2,
    Edit,
    Loader2,
    AlertTriangle,
    Users,
    FolderKanban,
    Clock,
    CalendarPlus,
    AlertCircle,
    HardDrive,
    RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

export default function WorkspaceManager() {
    const [workspaces, setWorkspaces] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [deleteModal, setDeleteModal] = useState({ open: false, workspace: null });
    const [editModal, setEditModal] = useState({ open: false, workspace: null });
    const [extendModal, setExtendModal] = useState({ open: false, workspace: null });
    const [editForm, setEditForm] = useState({ name: '', expires_at: '', storage_quota_gb: '' });
    const [backfilling, setBackfilling] = useState(false);
    const [extendDays, setExtendDays] = useState(30);

    useEffect(() => {
        fetchWorkspaces();
    }, []);

    const getToken = () => localStorage.getItem('superadmin_token');

    const fetchWorkspaces = async () => {
        try {
            const response = await axios.get(`${API_BASE}/api/superadmin/workspaces`, {
                headers: { Authorization: `Bearer ${getToken()}` }
            });
            setWorkspaces(response.data);
        } catch (error) {
            console.error('Failed to fetch workspaces:', error);
            toast.error('Gagal memuat data workspace');
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async () => {
        if (!deleteModal.workspace) return;
        
        try {
            await axios.delete(`${API_BASE}/api/superadmin/workspaces/${deleteModal.workspace.id}`, {
                headers: { Authorization: `Bearer ${getToken()}` }
            });
            toast.success('Workspace berhasil dihapus');
            setDeleteModal({ open: false, workspace: null });
            fetchWorkspaces();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Gagal menghapus workspace');
        }
    };

    const handleEdit = async () => {
        if (!editModal.workspace) return;

        try {
            const updateData = { name: editForm.name };
            if (editForm.expires_at) {
                updateData.expires_at = new Date(editForm.expires_at).toISOString();
            }
            if (editForm.storage_quota_gb !== '') {
                updateData.storage_quota_gb = editForm.storage_quota_gb === '0' || editForm.storage_quota_gb === ''
                    ? null
                    : parseFloat(editForm.storage_quota_gb);
            }

            await axios.put(`${API_BASE}/api/superadmin/workspaces/${editModal.workspace.id}`, updateData, {
                headers: { Authorization: `Bearer ${getToken()}` }
            });
            toast.success('Workspace berhasil diupdate');
            setEditModal({ open: false, workspace: null });
            fetchWorkspaces();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Gagal mengupdate workspace');
        }
    };

    const handleBackfill = async () => {
        setBackfilling(true);
        try {
            const res = await axios.post(`${API_BASE}/api/superadmin/storage/backfill-workspace-ids`, {}, {
                headers: { Authorization: `Bearer ${getToken()}` }
            });
            const { total, updated, failed, skipped } = res.data;
            toast.success(`Backfill selesai: ${updated} diupdate, ${failed} gagal, ${skipped} dilewati dari ${total} file`);
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Backfill gagal');
        } finally {
            setBackfilling(false);
        }
    };

    const handleExtend = async () => {
        if (!extendModal.workspace) return;
        
        try {
            await axios.post(`${API_BASE}/api/superadmin/workspaces/${extendModal.workspace.id}/extend`, 
                { days: extendDays },
                { headers: { Authorization: `Bearer ${getToken()}` } }
            );
            toast.success(`Workspace diperpanjang ${extendDays} hari`);
            setExtendModal({ open: false, workspace: null });
            setExtendDays(30);
            fetchWorkspaces();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Gagal memperpanjang workspace');
        }
    };

    const openEditModal = (workspace) => {
        const expiresDate = workspace.expires_at
            ? new Date(workspace.expires_at).toISOString().split('T')[0]
            : '';
        const quotaGb = workspace.storage_quota_bytes
            ? (workspace.storage_quota_bytes / (1024 ** 3)).toFixed(0)
            : '';
        setEditForm({ name: workspace.name, expires_at: expiresDate, storage_quota_gb: quotaGb });
        setEditModal({ open: true, workspace });
    };

    const formatQuota = (workspace) => {
        if (workspace.storage_quota_bytes) {
            const gb = (workspace.storage_quota_bytes / (1024 ** 3)).toFixed(0);
            return { label: `${gb} GB`, custom: true };
        }
        return { label: '30 GB', custom: false };
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        return new Date(dateStr).toLocaleDateString('id-ID', {
            day: 'numeric',
            month: 'short',
            year: 'numeric'
        });
    };

    const getExpirationStatus = (workspace) => {
        if (!workspace.expires_at) return { text: 'Tidak ada', color: 'text-slate-400' };
        
        const now = new Date();
        const expires = new Date(workspace.expires_at);
        const daysLeft = Math.ceil((expires - now) / (1000 * 60 * 60 * 24));
        
        if (workspace.is_expired || daysLeft < 0) {
            return { text: 'Expired', color: 'text-red-500', badge: 'bg-red-500/20 text-red-400' };
        } else if (daysLeft <= 7) {
            return { text: `${daysLeft} hari lagi`, color: 'text-amber-500', badge: 'bg-amber-500/20 text-amber-400' };
        } else {
            return { text: `${daysLeft} hari lagi`, color: 'text-green-500', badge: 'bg-green-500/20 text-green-400' };
        }
    };

    const filteredWorkspaces = workspaces.filter(ws => 
        ws.name?.toLowerCase().includes(searchQuery.toLowerCase())
    );

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-3xl font-bold text-white">Workspace Manager</h1>
                    <p className="text-slate-400 mt-1">Kelola semua workspace di sistem</p>
                </div>
                <Button
                    onClick={handleBackfill}
                    disabled={backfilling}
                    variant="outline"
                    className="border-slate-600 text-slate-300 hover:bg-slate-700 gap-2"
                >
                    {backfilling ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                        <RefreshCw className="w-4 h-4" />
                    )}
                    Backfill Storage Data
                </Button>
            </div>

            {/* Search & Stats */}
            <div className="flex items-center gap-4">
                <div className="relative flex-1 max-w-md">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-500" />
                    <Input
                        placeholder="Cari workspace..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="pl-10 bg-slate-800 border-slate-700 text-white"
                    />
                </div>
                <div className="flex items-center gap-2 text-slate-400">
                    <Building2 className="w-4 h-4" />
                    <span>{filteredWorkspaces.length} workspaces</span>
                </div>
            </div>

            {/* Workspaces Table */}
            <Card className="bg-slate-800 border-slate-700">
                <CardContent className="p-0">
                    <Table>
                        <TableHeader>
                            <TableRow className="border-slate-700 hover:bg-slate-700/50">
                                <TableHead className="text-slate-400">Nama</TableHead>
                                <TableHead className="text-slate-400">Owner</TableHead>
                                <TableHead className="text-slate-400">Members</TableHead>
                                <TableHead className="text-slate-400">Tasks</TableHead>
                                <TableHead className="text-slate-400">Storage Quota</TableHead>
                                <TableHead className="text-slate-400">Expires</TableHead>
                                <TableHead className="text-slate-400">Status</TableHead>
                                <TableHead className="text-slate-400 text-right">Aksi</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {filteredWorkspaces.map((ws) => {
                                const expStatus = getExpirationStatus(ws);
                                return (
                                    <TableRow key={ws.id} className="border-slate-700 hover:bg-slate-700/50">
                                        <TableCell className="text-white font-medium">{ws.name}</TableCell>
                                        <TableCell className="text-slate-300">{ws.owner_name || '-'}</TableCell>
                                        <TableCell>
                                            <div className="flex items-center gap-1 text-slate-300">
                                                <Users className="w-4 h-4" />
                                                {ws.member_count || 0}
                                            </div>
                                        </TableCell>
                                        <TableCell className="text-slate-300">{ws.task_count || 0}</TableCell>
                                        <TableCell>
                                            {(() => {
                                                const q = formatQuota(ws);
                                                return (
                                                    <div className="flex items-center gap-1.5">
                                                        <HardDrive className="w-3.5 h-3.5 text-slate-500" />
                                                        <span className="text-slate-300 text-sm">{q.label}</span>
                                                        {q.custom && (
                                                            <span className="px-1.5 py-0.5 rounded text-xs bg-amber-500/20 text-amber-400">Custom</span>
                                                        )}
                                                    </div>
                                                );
                                            })()}
                                        </TableCell>
                                        <TableCell className="text-slate-400">
                                            {formatDate(ws.expires_at)}
                                        </TableCell>
                                        <TableCell>
                                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${expStatus.badge || 'bg-slate-700 text-slate-400'}`}>
                                                {expStatus.text}
                                            </span>
                                        </TableCell>
                                        <TableCell className="text-right">
                                            <div className="flex items-center justify-end gap-1">
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    onClick={() => setExtendModal({ open: true, workspace: ws })}
                                                    className="text-slate-400 hover:text-green-500 hover:bg-green-500/10"
                                                    title="Perpanjang"
                                                >
                                                    <CalendarPlus className="w-4 h-4" />
                                                </Button>
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    onClick={() => openEditModal(ws)}
                                                    className="text-slate-400 hover:text-amber-500 hover:bg-amber-500/10"
                                                    title="Edit"
                                                >
                                                    <Edit className="w-4 h-4" />
                                                </Button>
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    onClick={() => setDeleteModal({ open: true, workspace: ws })}
                                                    className="text-slate-400 hover:text-red-500 hover:bg-red-500/10"
                                                    title="Hapus"
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </Button>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                            {filteredWorkspaces.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={8} className="text-center text-slate-500 py-8">
                                        Tidak ada workspace ditemukan
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </CardContent>
            </Card>

            {/* Delete Confirmation Modal */}
            <Dialog open={deleteModal.open} onOpenChange={(open) => setDeleteModal({ open, workspace: null })}>
                <DialogContent className="bg-slate-800 border-slate-700">
                    <DialogHeader>
                        <DialogTitle className="text-white flex items-center gap-2">
                            <AlertTriangle className="w-5 h-5 text-red-500" />
                            Konfirmasi Hapus
                        </DialogTitle>
                        <DialogDescription className="text-slate-400">
                            Apakah Anda yakin ingin menghapus workspace <strong className="text-white">{deleteModal.workspace?.name}</strong>? 
                            Semua data termasuk spaces, tasks, dan files akan ikut terhapus. Tindakan ini tidak dapat dibatalkan.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => setDeleteModal({ open: false, workspace: null })}
                            className="border-slate-600 text-slate-300 hover:bg-slate-700"
                        >
                            Batal
                        </Button>
                        <Button
                            onClick={handleDelete}
                            className="bg-red-500 hover:bg-red-600 text-white"
                        >
                            Hapus
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Edit Modal */}
            <Dialog open={editModal.open} onOpenChange={(open) => setEditModal({ open, workspace: null })}>
                <DialogContent className="bg-slate-800 border-slate-700">
                    <DialogHeader>
                        <DialogTitle className="text-white">Edit Workspace</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div className="space-y-2">
                            <Label className="text-slate-300">Nama Workspace</Label>
                            <Input
                                value={editForm.name}
                                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                                className="bg-slate-700 border-slate-600 text-white"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label className="text-slate-300">Tanggal Kadaluarsa</Label>
                            <Input
                                type="date"
                                value={editForm.expires_at}
                                onChange={(e) => setEditForm({ ...editForm, expires_at: e.target.value })}
                                className="bg-slate-700 border-slate-600 text-white"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label className="text-slate-300">Storage Quota (GB)</Label>
                            <Input
                                type="number"
                                min="0"
                                step="1"
                                value={editForm.storage_quota_gb}
                                onChange={(e) => setEditForm({ ...editForm, storage_quota_gb: e.target.value })}
                                placeholder="Kosongkan untuk default (30 GB)"
                                className="bg-slate-700 border-slate-600 text-white placeholder:text-slate-500"
                            />
                            <p className="text-xs text-slate-500">Isi 0 atau kosongkan untuk kembali ke default global (30 GB)</p>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => setEditModal({ open: false, workspace: null })}
                            className="border-slate-600 text-slate-300 hover:bg-slate-700"
                        >
                            Batal
                        </Button>
                        <Button
                            onClick={handleEdit}
                            className="bg-amber-500 hover:bg-amber-600 text-slate-900"
                        >
                            Simpan
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Extend Modal */}
            <Dialog open={extendModal.open} onOpenChange={(open) => { setExtendModal({ open, workspace: null }); setExtendDays(30); }}>
                <DialogContent className="bg-slate-800 border-slate-700">
                    <DialogHeader>
                        <DialogTitle className="text-white flex items-center gap-2">
                            <CalendarPlus className="w-5 h-5 text-green-500" />
                            Perpanjang Workspace
                        </DialogTitle>
                        <DialogDescription className="text-slate-400">
                            Perpanjang masa aktif workspace <strong className="text-white">{extendModal.workspace?.name}</strong>
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div className="p-3 bg-slate-700/50 rounded-lg">
                            <p className="text-sm text-slate-400">Expires saat ini:</p>
                            <p className="text-white font-medium">
                                {extendModal.workspace?.expires_at 
                                    ? formatDate(extendModal.workspace.expires_at)
                                    : 'Tidak diatur'}
                            </p>
                        </div>
                        <div className="space-y-2">
                            <Label className="text-slate-300">Perpanjang (hari)</Label>
                            <div className="flex gap-2">
                                {[30, 90, 180, 365].map((days) => (
                                    <Button
                                        key={days}
                                        variant={extendDays === days ? "default" : "outline"}
                                        size="sm"
                                        onClick={() => setExtendDays(days)}
                                        className={extendDays === days 
                                            ? "bg-amber-500 text-slate-900" 
                                            : "border-slate-600 text-slate-300 hover:bg-slate-700"}
                                    >
                                        {days === 365 ? '1 Tahun' : `${days} Hari`}
                                    </Button>
                                ))}
                            </div>
                            <Input
                                type="number"
                                value={extendDays}
                                onChange={(e) => setExtendDays(parseInt(e.target.value) || 0)}
                                className="bg-slate-700 border-slate-600 text-white mt-2"
                                placeholder="Atau masukkan jumlah hari..."
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => { setExtendModal({ open: false, workspace: null }); setExtendDays(30); }}
                            className="border-slate-600 text-slate-300 hover:bg-slate-700"
                        >
                            Batal
                        </Button>
                        <Button
                            onClick={handleExtend}
                            className="bg-green-500 hover:bg-green-600 text-white"
                        >
                            Perpanjang
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
